import React, { useState } from 'react';
import sendAcknowledmentWithAttachment from './acknowledgmentWordService';

const EmployeeAcknowledgment = ({ documentName, documentReference, processActivity, versionNumber, dateOfIssue }) => {
  const [formData, setFormData] = useState({
    documentReference: documentReference || '',
    processActivity: processActivity || '',
    versionNumber: versionNumber || '',
    dateOfIssue: dateOfIssue || '',
    employeeName: '',
    employeeEmail: '',
    jobRole: '',
    signature: '',
    employeeDate: '',
    supervisorName: '',
    supervisorSignature: '',
    supervisorDate: '',
    documentName: documentName || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await sendAcknowledmentWithAttachment(formData);
      setSubmitMessage('Employee acknowledgment submitted successfully!');
      
      setFormData(prev => ({
        ...prev,
        employeeName: '',
        employeeEmail: '',
        jobRole: '',
        signature: '',
        employeeDate: '',
        supervisorName: '',
        supervisorSignature: '',
        supervisorDate: ''
      }));
      
    } catch (error) {
      console.error('Error submitting acknowledgment:', error);
      setSubmitMessage('Error submitting acknowledgment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-no-archive className="mt-8 bg-pvs-accent/5 border border-pvs-accent/20 rounded-lg p-6 shadow-pvs-inner">
      <h2 className="text-xl font-bold text-white mb-4">
        Employee Acknowledgment of Risk Assessment & Method Statement (RAMS)
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Document Details Section - auto-populated, read-only for traceability */}
        <div className="pvs-glass p-4 rounded border border-pvs-border">
          <h3 className="text-lg font-semibold text-white mb-3">Document Details</h3>
          <p className="text-xs text-pvs-text-dim mb-3">
            The following details are taken directly from the controlled document and form part of the audit record.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Document Name</label>
              <div className="block w-full pvs-input text-sm bg-pvs-bg/40 cursor-not-allowed select-all">
                {formData.documentName || '—'}
              </div>
              <input type="hidden" name="documentName" value={formData.documentName} />
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Document Reference</label>
              <div className="block w-full pvs-input text-sm bg-pvs-bg/40 cursor-not-allowed select-all">
                {formData.documentReference || '—'}
              </div>
              <input type="hidden" name="documentReference" value={formData.documentReference} />
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Process/Activity Covered</label>
              <div className="block w-full pvs-input text-sm bg-pvs-bg/40 cursor-not-allowed select-all">
                {formData.processActivity || '—'}
              </div>
              <input type="hidden" name="processActivity" value={formData.processActivity} />
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Version Number</label>
              <div className="block w-full pvs-input text-sm bg-pvs-bg/40 cursor-not-allowed select-all">
                {formData.versionNumber || '—'}
              </div>
              <input type="hidden" name="versionNumber" value={formData.versionNumber} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Date of Issue</label>
              <div className="block w-full pvs-input text-sm bg-pvs-bg/40 cursor-not-allowed select-all">
                {formData.dateOfIssue || '—'}
              </div>
              <input type="hidden" name="dateOfIssue" value={formData.dateOfIssue} />
            </div>
          </div>
        </div>

        {/* Employee Declaration - aligned to HSWA 1974, MHSW Regs 1999, PUWER 1998, RIDDOR & ISO/IEC 17020 (UKAS) */}
        <div className="pvs-glass p-4 rounded border border-pvs-border">
          <h3 className="text-lg font-semibold text-white mb-3">Employee Declaration</h3>
          <div className="mb-2 text-sm text-pvs-text-muted">
            <p className="font-medium mb-2">By signing below I confirm that:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>I have read, understood and had the opportunity to ask questions on the contents of the above controlled document (Risk Assessment, Method Statement, Safe System of Work or Policy as applicable).</li>
              <li>I have received and understood the appropriate instruction, information and training to carry out the activity safely and competently, including the safe use of any work equipment provided (Reg. 9, PUWER 1998).</li>
              <li>I will take reasonable care for the health and safety of myself and of other persons who may be affected by my acts or omissions, in accordance with my duties under <strong>Section 7 of the Health &amp; Safety at Work etc. Act 1974</strong>.</li>
              <li>I will co-operate with my employer so far as is necessary to enable any statutory duty to be performed and will not intentionally or recklessly interfere with or misuse anything provided in the interests of health, safety or welfare (<strong>Sections 7(b) &amp; 8 HSWA 1974</strong>).</li>
              <li>I will use any equipment, safety devices, personal protective equipment (PPE) and materials in accordance with the training, instructions and information I have received (<strong>Reg. 14, Management of Health and Safety at Work Regulations 1999</strong>; PPER 1992).</li>
              <li>I will inform my supervisor or a Director immediately of any work situation, defect, near-miss, accident, incident, dangerous occurrence or shortcoming in protective measures, in line with PVS reporting procedures and <strong>RIDDOR 2013</strong> where applicable.</li>
              <li>I understand that I must only carry out work covered by this document if I am authorised and competent to do so, and I will stop work and seek clarification before proceeding if any part of the document, hazard or control measure is unclear.</li>
              <li>I will work strictly in accordance with the applicable Written Scheme of Examination (WSE), procedures and acceptance criteria, and will not deviate from approved methods without written authorisation.</li>
              <li>I will act <strong>impartially and objectively</strong>, basing all inspection, refurbishment and quality decisions solely on factual evidence and documented requirements, and will declare any actual, potential or perceived conflict of interest to a Director without delay (ISO/IEC 17020).</li>
              <li>I will treat all client and company information, inspection outcomes, technical data and personal data as <strong>confidential</strong> and will only access or disclose such information on a strict need-to-know basis in accordance with PVS confidentiality and data-protection procedures.</li>
              <li>I acknowledge that this signed record may be retained by PVS and shared with regulators, accreditation bodies (including UKAS) and clients as evidence of competence, briefing and document control.</li>
            </ul>
          </div>
          <p className="text-xs text-pvs-text-dim mt-3">
            This declaration supports compliance with the Health &amp; Safety at Work etc. Act 1974, the Management of Health and Safety at Work Regulations 1999, PUWER 1998, PPER 1992, RIDDOR 2013 and the impartiality, confidentiality and competence requirements of ISO/IEC 17020 as assessed by UKAS.
          </p>
        </div>

        {/* Employee Details */}
        <div className="pvs-glass p-4 rounded border border-pvs-border">
          <h3 className="text-lg font-semibold text-white mb-3">Employee Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Name *</label>
              <input type="text" name="employeeName" value={formData.employeeName} onChange={handleInputChange} className="block w-full pvs-input text-sm" placeholder="Employee full name" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Email *</label>
              <input type="email" name="employeeEmail" value={formData.employeeEmail} onChange={handleInputChange} className="block w-full pvs-input text-sm" placeholder="employee@email.com" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Job Role *</label>
              <input type="text" name="jobRole" value={formData.jobRole} onChange={handleInputChange} className="block w-full pvs-input text-sm" placeholder="Job title/role" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Date *</label>
              <input type="date" name="employeeDate" value={formData.employeeDate} onChange={handleInputChange} className="block w-full pvs-input text-sm" required />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Signature *</label>
              <input type="text" name="signature" value={formData.signature} onChange={handleInputChange} className="block w-full pvs-input text-sm" placeholder="Digital signature or typed name" required />
            </div>
          </div>
        </div>

        {/* Supervisor/Manager Verification */}
        <div className="pvs-glass p-4 rounded border border-pvs-border">
          <h3 className="text-lg font-semibold text-white mb-3">Supervisor/Manager Verification (if required)</h3>
          <p className="text-sm text-pvs-text-dim mb-4">
            I confirm that the above employee has been briefed on this RAMS and is competent to undertake the activity described.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Supervisor Name</label>
              <input type="text" name="supervisorName" value={formData.supervisorName} onChange={handleInputChange} className="block w-full pvs-input text-sm" placeholder="Supervisor full name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Signature</label>
              <input type="text" name="supervisorSignature" value={formData.supervisorSignature} onChange={handleInputChange} className="block w-full pvs-input text-sm" placeholder="Digital signature" />
            </div>
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-1">Date</label>
              <input type="date" name="supervisorDate" value={formData.supervisorDate} onChange={handleInputChange} className="block w-full pvs-input text-sm" />
            </div>
          </div>
        </div>

        {/* Submit Message */}
        {submitMessage && (
          <div className={`p-4 rounded-md ${submitMessage.includes('Error') ? 'bg-red-900/30 border border-red-500/30 text-red-400' : 'bg-green-900/30 border border-green-500/30 text-pvs-accent'}`}>
            {submitMessage}
          </div>
        )}

        <p className="text-xs text-pvs-text-dim text-center">
          A PDF copy of this acknowledgment will be emailed for record-keeping.
        </p>

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-pvs-accent text-pvs-bg font-semibold rounded-md shadow-sm hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Employee Acknowledgment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeAcknowledgment;
